// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useRef, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import getConfig from 'next/config';
import { useRouter } from 'next/router';

import Cookies from 'js-cookie';

import { EstimateActions, EstimateType } from '@zego/enums';
import { handleStartJourney } from '@zego/flows/startJourneyUtil';
import useAttribution from '@zego/hooks/useAttribution';
import useLogout from '@zego/hooks/useLogout';
import useStableCallback from '@zego/hooks/useStableCallback';
import sentry from '@zego/sentry';
import { useTracking } from '@zego/tracking';
import { withTrackingScope } from '@zego/tracking';

import QuickEstimatesAuthModal from '../../forms/QuickEstimatesAuthModal';
import createUseEstimateForm from '../../hooks/createUseEstimateForm';
import { setPromotionRefUrl } from '../../utils';
import useScooterEstimateResult from '../useScooterEstimateResult';
import ScooterQuickEstimatesFormCore from './ScooterQuickEstimatesFormCore';

const {
  publicRuntimeConfig: {
    ONBOARDING_WEB_HOSTNAME,
    SCOOTER_JOURNEY_SESSION_COOKIE_NAME,
    GRAPHQL_ONBOARDING_API,
  },
} = getConfig();

function ScooterQuickEstimatesForm({
  viewer,
  scooterMicroFrontendFlag,
  ...props
}) {
  const logout = useLogout();
  const [error, setError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const attribution = useAttribution();
  const { trackEvent } = useTracking();
  const router = useRouter();

  const [submittingKind, setSubmittingKind] = useState(null);

  const deferredSubmit = useRef(null);

  const openModal = useCallback(() => setModalIsOpen(true), []);
  const closeModal = useCallback(() => setModalIsOpen(false), []);

  const scooterCookie = Cookies.get(SCOOTER_JOURNEY_SESSION_COOKIE_NAME);
  const isScooterCookie = !!scooterCookie;
  const errorLabel = 'Something went wrong';

  const name = viewer?.currentUser?.fullName;
  const isLoggedIn = !!name;
  const isScooterMicroFrontEndFlagOn =
    scooterMicroFrontendFlag?.variation.value;

  const nextUrl = isScooterMicroFrontEndFlagOn
    ? `${ONBOARDING_WEB_HOSTNAME}/scooter/`
    : '/scooter/estimates';
  const forUseWithUrl = createUseEstimateForm(
    nextUrl,
    useScooterEstimateResult,
  );

  const _onSubmit = useStableCallback(
    forUseWithUrl(null, values => ({
      action: EstimateActions.UPDATE_DATA,
      data: {
        person: {
          dateOfBirth: values.bday,
          postcode: values.postcode.trim(),
        },
        vehicle: {
          registrationNumber: values.registration,
        },
      },
      attribution,
    })),
    [],
  );

  const wrappedSubmit = useCallback(
    (values, onComplete) => {
      setPromotionRefUrl(props.locationName);
      trackEvent('Website: Quick Estimates - Short form submitted');

      _onSubmit(values, {
        onError: err => {
          if (err) {
            sentry.captureException(err);
          }

          setModalIsOpen(false);
          setError(errorLabel);
          onComplete();
          setSubmittingKind(null);
        },
      });
    },
    [_onSubmit, trackEvent],
  );

  const handleRedirect = () =>
    router.push(`${ONBOARDING_WEB_HOSTNAME}/scooter/`);

  const handleStartScooterJourney = async values => {
    const { bday, postcode, registration } = values;

    const initial_data = {
      meta: [],
      data: [
        { name: 'dob', value: bday },
        { name: 'postcode', value: postcode },
        { name: 'registration', value: registration },
      ],
    };

    try {
      handleStartJourney(
        initial_data,
        GRAPHQL_ONBOARDING_API,
        SCOOTER_JOURNEY_SESSION_COOKIE_NAME,
        `${ONBOARDING_WEB_HOSTNAME}`,
      );
      handleRedirect();
    } catch {
      setError(errorLabel);
    }
  };

  const handleScooterJourney = values =>
    isScooterCookie ? handleRedirect() : handleStartScooterJourney(values);

  const _anonymousOnSubmit = useCallback(
    (values, cb) => {
      setSubmittingKind('anonymous');

      if (isScooterMicroFrontEndFlagOn) {
        handleScooterJourney(values);
      } else {
        wrappedSubmit(values, cb);
      }
    },
    [wrappedSubmit],
  );

  const _authenticatedOnSubmit = useCallback(
    (values, cb) => {
      if (isScooterMicroFrontEndFlagOn) {
        handleScooterJourney(values);
      } else {
        deferredSubmit.current = () => {
          wrappedSubmit(values, cb);
        };
        openModal();
      }
    },
    [openModal, wrappedSubmit],
  );

  return (
    <>
      <ScooterQuickEstimatesFormCore
        {...props}
        error={error}
        submitting={submittingKind === 'anonymous'}
        onSubmit={isLoggedIn ? _authenticatedOnSubmit : _anonymousOnSubmit}
      />
      <QuickEstimatesAuthModal
        name={name}
        isOpen={modalIsOpen}
        onClose={closeModal}
        onAccept={() => {
          setSubmittingKind('continue');
          deferredSubmit.current();
        }}
        onReject={async () => {
          setSubmittingKind('logout');
          await logout();
          deferredSubmit.current();
        }}
        isAccepting={submittingKind === 'continue'}
        isRejecting={submittingKind === 'logout'}
      />
    </>
  );
}

const ScooterQuickEstimatesFormContainer = createFragmentContainer(
  ScooterQuickEstimatesForm,
  {
    viewer: graphql`
      fragment ScooterQuickEstimatesForm_viewer on Viewer {
        currentUser {
          fullName
        }
      }
    `,
  },
);

export default withTrackingScope({
  estimateType: EstimateType.GB_SCOOTER,
  featureName: 'Scooter Quick Estimates Widget',
})(ScooterQuickEstimatesFormContainer);
